import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import CopyButton from '../../components/ui/CopyButton.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { EmptyState, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import ProviderLogo from '../../components/ui/ProviderLogo.jsx';
import { fmtDateTime } from '../../utils/format.js';
import { checkRequired } from '../../utils/validate.js';

const KINDS = [
  ['Your normal mailbox', 'An everyday email from Outlook or Gmail'],
  ['Newsletters and marketing', 'From Mailchimp, HubSpot or whatever you use'],
  ['Invoices and receipts', 'From your accounts system'],
  ['Tickets and registration', 'Confirmations from your ticketing platform'],
];

function RenameModal({ source, base, onClose, onSaved }) {
  const [name, setName] = useState(source.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const save = async () => {
    const problem = checkRequired(name, 'a name, e.g. “Mailchimp — newsletters”', { max: 200 });
    if (problem) { setFieldError(problem); return; }
    setBusy(true); setError('');
    try { await api(`${base}/${source.id}`, { method: 'PATCH', body: { name, confirmed: true } }); onSaved(); } catch (err) {
      if (err.data?.fields?.name) setFieldError(err.data.fields.name); else setError(err.message);
      setBusy(false);
    }
  };
  return (
    <Modal title="Name this service" description="So your team knows what it is, e.g. “Mailchimp — newsletters”. Saving also confirms it’s one you use, so email from it can be verified." onClose={onClose} onSubmit={save} busy={busy}
      footer={<ModalActions submitLabel="Save and confirm" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Name" required maxLength={200} value={name} error={fieldError} onChange={(e) => { setName(e.target.value); setFieldError(''); }} />
    </Modal>
  );
}

/** The services the company really sends email through, and how we learn them. */
export default function EmailServices() {
  const { organisation: current } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [notice, setNotice] = useState('');
  const base = current ? `/portal/organisations/${current.id}/email-services` : '';

  const load = useCallback(async () => {
    if (!base) return;
    try { setData(await api(base)); } catch (err) { setError(err.message); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const [busyProvider, setBusyProvider] = useState(null);
  const dismissSuggestion = async (s) => {
    setError(''); setBusyProvider(s.provider);
    try {
      await api(`${base}/dismiss`, { method: 'POST', body: { provider: s.provider } });
      setNotice(`${s.provider} won’t be suggested again.`);
      await load();
    } catch (err) { setError(err.message); } finally { setBusyProvider(null); }
  };
  const confirmSuggestion = async (s) => {
    setBusyProvider(s.provider);
    setError('');
    try {
      await api(base, { method: 'POST', body: { provider: s.provider } });
      setNotice(`${s.provider} added to your services.`);
      await load();
    } catch (err) { setError(err.message); } finally { setBusyProvider(null); }
  };

  const back = { to: '/company', label: 'Company' };
  if (error && !data) return <div className="page"><PageHeader back={back} title="Your email services" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const canManage = data.organisation.can_manage;

  return (
    <div className="page">
      <PageHeader back={back} title="Your email services"
        subtitle="The services you really send email through: your mailbox, your newsletter tool, invoices. Why it matters: a criminal who gets into an old newsletter account can send email that passes every usual check. If it comes through a service that isn’t on this list, we say it can’t be verified." />
      {notice && <Alert tone="success">{notice}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      <Section title="Teach us what your email looks like"
        description="Send (don’t forward) one real email from each system you use to this address. We read how it was sent and remember it.">
        {data.can_learn ? (
          <>
            <div className="copy-row">
              <code className="mono strong">{data.learn_address}</code>
              <CopyButton text={data.learn_address} />
            </div>
            <ul className="check-list learn-kinds">
              {KINDS.map(([title, hint]) => <li key={title}><strong>{title}</strong> <span className="muted small">— {hint}</span></li>)}
            </ul>
            <p className="muted small">We only learn from email we can prove came from one of your verified domains, so nobody else can teach us that their email is yours. Send it to us, or just add us as a Cc or Bcc on a real campaign — a forwarded email doesn’t count. We email your admins when we learn a new service, and every email we receive appears in your <Link to="/outbox">Outbox</Link>.</p>
          </>
        ) : (
          <Alert tone="warn">Verify one of your domains first: we only learn from email we can prove came from you.</Alert>
        )}
      </Section>

      <Section title="Your services" flush={data.sources.length > 0}>
        {data.sources.length === 0 ? (
          <EmptyState title="None yet">Send an email to the address above, or confirm one of the services we found below.</EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Service</th><th>How we know</th><th>Emails seen</th><th>Status</th>{canManage && <th><span className="sr-only">Actions</span></th>}</tr></thead>
              <tbody>
                {data.sources.map((s) => (
                  <tr key={s.id}>
                    <td data-label="Service">
                      <span className="provider-name"><ProviderLogo name={s.provider} size={20} /> <strong>{s.name}</strong></span>
                      {(s.dkim_domains.length > 0 || s.return_path_domains.length > 0) && (
                        <div className="muted small">Signs as {s.dkim_domains.join(', ') || '—'}{s.return_path_domains.length > 0 && <> · returns undelivered mail to {s.return_path_domains.join(', ')}</>}</div>
                      )}
                    </td>
                    <td data-label="How we know">{s.origin === 'learned_from_mail' ? 'You sent us an email' : 'You confirmed it'}</td>
                    <td data-label="Emails seen">{s.sample_count}{s.sample_count > 0 && <div className="muted small">last {fmtDateTime(s.last_seen)}</div>}</td>
                    <td data-label="Status">{s.confirmed ? <Pill tone="info">Confirmed by you</Pill> : <Pill tone="warn">Needs a name</Pill>}</td>
                    {canManage && (
                      <td className="row-actions">
                        <button type="button" className="btn btn-secondary btn-small" onClick={() => setRenaming(s)}>{s.confirmed ? 'Rename' : 'Name and confirm'}</button>
                        <button type="button" className="btn btn-danger-ghost btn-small" onClick={() => setRemoving(s)}>Remove</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {data.suggestions.length > 0 && (
        <Section title="Services we found" description="Your domains’ settings allow these services to send email as you (or to run your mailbox). Confirm the ones you really use. Say “Not ours” to the rest — an unknown service allowed to send as you is worth asking your IT team about.">
          <ul className="suggestion-list">
            {data.suggestions.map((s) => (
              <li key={s.provider}>
                <span className="provider-name"><ProviderLogo name={s.provider} size={20} /> <strong>{s.provider}</strong></span>
                <span className="muted small">{s.why} ({s.domains.join(', ')})</span>
                {canManage && (
                  <span className="suggestion-actions">
                    <button type="button" className="btn btn-secondary btn-small" disabled={busyProvider === s.provider} onClick={() => confirmSuggestion(s)}>Yes, we use this</button>
                    <button type="button" className="btn btn-danger-ghost btn-small" disabled={busyProvider === s.provider} onClick={() => dismissSuggestion(s)}>Not ours</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {renaming && <RenameModal source={renaming} base={base} onClose={() => setRenaming(null)} onSaved={() => { setRenaming(null); load(); }} />}
      {removing && (
        <ConfirmModal title={`Remove ${removing.name}?`} confirmLabel="Remove" danger onClose={() => setRemoving(null)}
          onConfirm={async () => { await api(`${base}/${removing.id}`, { method: 'DELETE' }); setRemoving(null); load(); }}>
          Email sent through this service will no longer count as one of yours. You can teach us again by sending another email.
        </ConfirmModal>
      )}
    </div>
  );
}
